import React, { useEffect, useState } from "react";
import { auth, firestore as db } from "../FirebaseConfig";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast"; // Import toast for notifications

const Account = () => {
  const [user, setUser] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const API_URL = "https://us-central1-tghc-web.cloudfunctions.net"; // Replace with your Firebase API URL


  const [newAddress, setNewAddress] = useState({
    customerName: "",
    addressLine1: "",
    addressLine2: "",
    email: "",
    phone: "",
    City: "",
    State: "",
    Country: "India",
    Pincode: "",
  });
  const [pincodeFetched, setPincodeFetched] = useState(false);


  useEffect(() => {
    // Only trigger when Pincode is exactly 6 digits and not fetched already
    const pincode = newAddress.Pincode;
    if (/^\d{6}$/.test(pincode) && !pincodeFetched) {
      fetchCityStateForPincode(pincode);
    }
    // Reset fetched flag if pincode is cleared or changed to less than 6 digits
    if (pincode.length !== 6 && pincodeFetched) {
      setPincodeFetched(false);
      setNewAddress(addr => ({
        ...addr,
        City: "",
        State: ""
      }));
    }
  }, [newAddress.Pincode]);



  const fetchCityStateForPincode = async (pincode) => {
    try {
      const token = await user.getIdToken(true);
      const response = await fetch(
        `${API_URL}/checkPincodeServiceability?pincode=${pincode}`,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      const data = await response.json();
      if (data.serviceable) {
        setNewAddress(addr => ({
          ...addr,
          City: data.city || "",
          State: data.state || ""
        }));
        setPincodeFetched(true);
        toast.success(`Autofilled city/state for pincode!`);
      } else {
        toast.dismiss();
        toast.error("This Pincode is not serviceable.");
        setNewAddress(addr => ({
          ...addr,
          City: "",
          State: ""
        }));
        setPincodeFetched(false);
      }
    } catch (error) {
      toast.error("Failed to fetch city/state.");
      setPincodeFetched(false);
    }
  };



  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const redirectPath =
      new URLSearchParams(location.search).get("redirect") || "/account";

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setNewAddress({ ...newAddress, email: currentUser.email });

        const userRef = doc(db, "customers", currentUser.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          setCustomer(userDoc.data());
        } else {
          await setDoc(userRef, { address: [], defaultAddress: -1 });
          setCustomer({ address: [], defaultAddress: -1 });
        }
      } else {
        setUser(null);
        setCustomer(null);
        navigate(`/login?redirect=${redirectPath}`);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const handleAddOrEditAddress = async (e) => {
    e.preventDefault();
    if (
      !newAddress.customerName ||
      !newAddress.addressLine1 ||
      !newAddress.addressLine2 ||
      !newAddress.phone ||
      !newAddress.City ||
      !newAddress.State ||
      !newAddress.Pincode
    ) {
      toast.error("Please fill in all required fields.");
      // return;
    }
    toast.loading("Adding/Updating Address");
    const cleanPincode = newAddress.Pincode.replace(/\s+/g, "");
    const isServiceable = await checkPincodeServiceability(cleanPincode);
    
    // Check if the Pincode is serviceable before proceeding
    if (!isServiceable) {
      toast.error("This Pincode is not serviceable. Please enter a valid Pincode.");
      return;
    }


    const queryParams = new URLSearchParams(location.search);
    const redirectPath = queryParams.get("redirect") || "/";

  
    if (customer) {
      const customerRef = doc(db, "customers", user.uid);
      let updatedAddresses = [...(customer.address || [])];
  
      if (editIndex !== null) {
        updatedAddresses[editIndex] = newAddress;
        toast.dismiss();
        toast.success("Address updated successfully!");
      } else {
        if (updatedAddresses.length >= 2) {
          toast.dismiss();

          toast.error("You can only add up to 2 addresses.");
          return;
        }
        updatedAddresses.push(newAddress);
        toast.dismiss();

        toast.success("Address added successfully!");
      }
  
      try {
        await updateDoc(customerRef, {
          address: updatedAddresses,
        });
  
        setCustomer({
          ...customer,
          address: updatedAddresses,
        });
  
        navigate(redirectPath);

        setShowAddressForm(false);
        setEditIndex(null);
        setNewAddress({
          customerName: "",
          addressLine1: "",
          addressLine2: "",
          phone: "",
          City: "",
          State: "",
          Country: "India",
          Pincode: "",
        });
      } catch (error) {
        console.error("Error saving address:", error);
        alert("Error saving address. Please try again.");
      }
    }
  };
  

  const handleEditAddress = (index) => {
    setEditIndex(index);
    setNewAddress(customer.address[index]);
    setShowAddressForm(true);
  };


  const checkPincodeServiceability = async (pincode) => {
    try {
      const token = await user.getIdToken(true);
      const response = await fetch(
        `${API_URL}/checkPincodeServiceability?pincode=${pincode}`,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      const data = await response.json();
      if (data.serviceable) {
        // Optionally autofill city/state:
        setNewAddress(addr => ({
          ...addr,
          City: data.city || "",
          State: data.state || ""
        }));
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Error checking pincode:", error);
      return false;
    }
  };
  
  


  const handleSetDefaultAddress = async (index) => {
    if (customer) {
      const customerRef = doc(db, "customers", auth.currentUser.uid);

      try {
        await updateDoc(customerRef, { defaultAddress: index });

        setCustomer({
          ...customer,
          defaultAddress: index,
        });

        toast.success("Default address updated!");

        const redirectPath = new URLSearchParams(location.search).get("redirect");
        if (redirectPath) {
          navigate(redirectPath);
        }
      } catch (error) {
        console.error("Error updating default address:", error);
        alert("Failed to update address. Please try again.");
      }
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex justify-center items-center">Loading...</div>
    );
  }

  return (
    <div className="max-w-4xl mt-27 mx-auto p-6">
      <h1 className="text-3xl font-semibold text-gray-800">My Account</h1>

      {user ? (
        <div className="bg-white p-6 rounded-lg shadow-md mt-4">
          <div className="mt-6">
            <h2 className="text-lg font-semibold text-gray-700">Address</h2>
            {customer?.address && customer.address.length > 0 ? (
              <div className="space-y-4 mt-2">
                {customer.address.map((addr, index) => (
                  <div key={index} className="bg-gray-100 p-4 rounded-lg flex items-center justify-between">
                    <input
                      type="radio"
                      name="defaultAddress"
                      checked={customer.defaultAddress === index}
                      onChange={() => handleSetDefaultAddress(index)}
                      className="h-5 w-5 text-orange-500 mr-2"
                    />
                    <div>
                      <p className="font-semibold">{addr.customerName}</p>
                      <p>{addr.addressLine1}</p>
                      <p>{addr.addressLine2}</p>
                      <p>{addr.City}, {addr.State}, {addr.Pincode}</p>
                      <p>{addr.phone}</p>
                      <p>{addr.Country || "India"}</p>
                    </div>
                    <button
                      onClick={() => handleEditAddress(index)}
                      className="text-blue-500 hover:underline"
                    >
                      Edit
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 mt-2">No address available</p>
            )}

            {(customer?.address?.length < 3 || !customer?.address)  && (
              <button
                onClick={() => setShowAddressForm(true)}
                className="mt-4 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition"
              >
                Add Address
              </button>
            )}
          </div>

          {/* Address Modal */}
          {showAddressForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full">
                <h2 className="text-lg font-semibold text-gray-700">
                  {editIndex !== null ? "Edit Address" : "Add Address"}
                </h2>
                <form onSubmit={handleAddOrEditAddress} className="space-y-3">
                <input
  name="customerName"
  autoComplete="name"
  type="text"
  placeholder="Full Name"
  value={newAddress.customerName}
  onChange={(e) => setNewAddress({ ...newAddress, customerName: e.target.value })}
  className="w-full p-2 border rounded"
  required
/>
<input
  name="phone"
  autoComplete="tel"
  type="tel"
  placeholder="Phone"
  value={newAddress.phone}
  onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
  className="w-full p-2 border rounded"
  required
/>
<input
  name="addressLine1"
  autoComplete="address-line1"
  type="text"
  placeholder="Address Line 1"
  value={newAddress.addressLine1}
  onChange={(e) => setNewAddress({ ...newAddress, addressLine1: e.target.value })}
  className="w-full p-2 border rounded"
  required
/>
<input
  name="addressLine2"
  autoComplete="address-line2"
  type="text"
  placeholder="Address Line 2"
  value={newAddress.addressLine2}
  onChange={(e) => setNewAddress({ ...newAddress, addressLine2: e.target.value })}
  className="w-full p-2 border rounded"
  required
/>

<input
  name="Pincode"
  autoComplete="postal-code"
  type="text"
  placeholder="Pincode"
  value={newAddress.Pincode}
  onChange={(e) =>
    setNewAddress({
      ...newAddress,
      Pincode: e.target.value.replace(/\s+/g, ""),
    })
  }
  className="w-full p-2 border rounded"
  required
/>


<input
  name="City"
  autoComplete="address-level2"
  type="text"
  placeholder="City"
  value={newAddress.City}
  onChange={(e) => setNewAddress({ ...newAddress, City: e.target.value })}
  className="w-full p-2 border rounded"
  required
/>
<input
  name="State"
  autoComplete="address-level1"
  type="text"
  placeholder="State"
  value={newAddress.State}
  onChange={(e) => setNewAddress({ ...newAddress, State: e.target.value })}
  className="w-full p-2 border rounded"
  required
/>

   <button type="submit" className="w-full bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition">Save</button>
                  <button type="button" onClick={() => setShowAddressForm(false)} className="w-full bg-gray-300 text-gray-700 px-4 py-2 rounded-lg mt-2">Cancel</button>
                </form>
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="text-gray-700 mt-20 mb-20">Please log in to view account details.</p>
      )}
    </div>
  );
};

export default Account;
